import { getFormName } from "./form-analytics";

/** Code fallback when metafields are not configured */
const DEFAULT_MENS_QUANTITY_DELIVERY_DAYS: Record<number, number> = {
  10: 30,
  20: 60,
};

const DAYS_PER_WEEK = 7;
const DAYS_PER_MONTH = 30;

export type FrequencyTierMap = Record<number, number>;

export function isMensProduct(productHandle: string): boolean {
  const formName = getFormName(productHandle);
  return formName === "para_el" || formName === "for_him";
}

export function extractQuantityCount(quantityLabel: string): number | null {
  const match = quantityLabel.match(/\d+/);
  return match ? parseInt(match[0], 10) : null;
}

export function parseFrequencyTiersMetafield(
  value: string | null | undefined
): FrequencyTierMap | null {
  if (!value?.trim()) return null;

  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    const tiers: FrequencyTierMap = {};
    for (const [quantity, days] of Object.entries(parsed)) {
      const quantityCount = parseInt(quantity, 10);
      const deliveryDays = Number(days);
      if (!Number.isNaN(quantityCount) && !Number.isNaN(deliveryDays)) {
        tiers[quantityCount] = deliveryDays;
      }
    }

    return Object.keys(tiers).length > 0 ? tiers : null;
  } catch {
    return null;
  }
}

export function getRecommendedDeliveryDays(
  quantityLabel: string,
  options?: {
    variantDeliveryDays?: number | null;
    productTiers?: FrequencyTierMap | null;
    fallbackTiers?: FrequencyTierMap;
  }
): number | null {
  const count = extractQuantityCount(quantityLabel);
  if (!count) return null;

  if (
    options?.variantDeliveryDays != null &&
    !Number.isNaN(options.variantDeliveryDays)
  ) {
    return options.variantDeliveryDays;
  }

  if (options?.productTiers?.[count] != null) {
    return options.productTiers[count];
  }

  const fallbackTiers = options?.fallbackTiers ?? DEFAULT_MENS_QUANTITY_DELIVERY_DAYS;
  return fallbackTiers[count] ?? null;
}

function intervalToDays(interval?: string, intervalCount?: number): number | null {
  if (!interval || !intervalCount) return null;

  switch (interval) {
    case "DAY":
      return intervalCount;
    case "WEEK":
      return intervalCount * DAYS_PER_WEEK;
    case "MONTH":
      return intervalCount * DAYS_PER_MONTH;
    default:
      return null;
  }
}

function parseDaysFromText(text: string): number | null {
  const normalized = text.replace(/-/g, " ");

  const dayMatch = normalized.match(/(\d+)\s*(?:days?|d[ií]as?)/i);
  if (dayMatch) return parseInt(dayMatch[1], 10);

  const weekMatch = normalized.match(/(\d+)\s*(?:weeks?|semanas?)/i);
  if (weekMatch) return parseInt(weekMatch[1], 10) * DAYS_PER_WEEK;

  const monthMatch = normalized.match(/(\d+)\s*(?:months?|mes(?:es)?)/i);
  if (monthMatch) return parseInt(monthMatch[1], 10) * DAYS_PER_MONTH;

  if (/\b(monthly|mensual)\b/i.test(normalized)) {
    return DAYS_PER_MONTH;
  }

  return null;
}

export function getPlanDeliveryDays(plan: {
  deliveryPolicy?: {
    interval?: string;
    intervalCount?: number;
  };
  billingPolicy?: {
    interval?: string;
    intervalCount?: number;
  };
  options?: Array<{ name?: string; value?: string }>;
  name?: string;
}): number | null {
  const deliveryDays = intervalToDays(
    plan.deliveryPolicy?.interval,
    plan.deliveryPolicy?.intervalCount
  );
  if (deliveryDays) return deliveryDays;

  const billingDays = intervalToDays(
    plan.billingPolicy?.interval,
    plan.billingPolicy?.intervalCount
  );
  if (billingDays) return billingDays;

  for (const option of plan.options ?? []) {
    const optionDays = parseDaysFromText(option.value ?? "");
    if (optionDays) return optionDays;
  }

  if (plan.name) {
    return parseDaysFromText(plan.name);
  }

  return null;
}

export function findSellingPlanByDeliveryDays(
  sellingPlans: Array<{ node: any }>,
  deliveryDays: number
): any | null {
  const exactMatch = sellingPlans.find(
    (edge) => getPlanDeliveryDays(edge.node) === deliveryDays
  );
  if (exactMatch) return exactMatch.node;

  return (
    sellingPlans.find((edge) => {
      const planDays = getPlanDeliveryDays(edge.node);
      return planDays !== null && Math.abs(planDays - deliveryDays) < 1;
    })?.node ?? null
  );
}

export function getRecommendedSellingPlan(
  sellingPlans: Array<{ node: any }>,
  quantityLabel: string,
  options?: {
    variantDeliveryDays?: number | null;
    productTiers?: FrequencyTierMap | null;
    fallbackTiers?: FrequencyTierMap;
  }
): any | null {
  const deliveryDays = getRecommendedDeliveryDays(quantityLabel, options);
  if (!deliveryDays) return null;
  return findSellingPlanByDeliveryDays(sellingPlans, deliveryDays);
}

export function getVariantRecommendedDeliveryDays(
  variant: { metafields?: Array<{ key?: string; value?: string } | null> | null } | null | undefined
): number | null {
  const rawValue = variant?.metafields?.find(
    (mf) => mf?.key === "recommended_delivery_days"
  )?.value;

  if (!rawValue) return null;

  const days = parseInt(rawValue, 10);
  return Number.isNaN(days) ? null : days;
}

export function isRecommendedPlanForDeliveryDays(
  plan: Parameters<typeof getPlanDeliveryDays>[0],
  recommendedDeliveryDays: number | null
): boolean {
  if (recommendedDeliveryDays == null) return false;
  const planDays = getPlanDeliveryDays(plan);
  return planDays === recommendedDeliveryDays;
}
